import { buildEndNodeId } from "@bliks-io/contracts";
import i18next from "i18next";
import { applySwimlaneLayout, buildLanePlaceholders } from "./layout";
import { getAssigneeName } from "./utils";
import { isAssigneeDeleted } from "../../../../utils/deleted-entity-utils";
import { FLOW_EVENT_NODE_TYPE, EVENT_NODE_ID_PREFIX, buildEventNodeId, buildGatewayNodeId, parseGatewayNodeId, isEndNodeId } from "./constants";
import { buildRuleHandleId } from "./edgeClassifier";
import { groupRulesByOutput } from "../../decision-table/decisionTableColumns";
/**
 * Build an {@link AssigneeLookup} from the org's four entity lists — the single
 * place the id → entity maps are constructed, shared by every surface that
 * assembles the lookup from its own transport (the frontend's `useAssigneeLookup`
 * over the session client, the app-sdk's `useAssigneeLookup` over the app
 * client). Pure: callers own fetching and degradation; an omitted/empty list
 * yields an empty map, which `lookupAssignee` degrades from gracefully.
 */ export function buildAssigneeLookup(lists) {
    return {
        users: new Map((lists.users ?? []).map((u)=>[
                u._id,
                u
            ])),
        orgFunctions: new Map((lists.orgFunctions ?? []).map((f)=>[
                f._id,
                f
            ])),
        systems: new Map((lists.systems ?? []).map((s)=>[
                s._id,
                s
            ])),
        externalContacts: new Map((lists.externalContacts ?? []).map((c)=>[
                c._id,
                c
            ]))
    };
}
/**
 * Build the canvas's `SuggestionRefInfo` map from a flat EntitySuggestion list.
 * Single seam shared by every flow surface (visualization + editor) so the
 * proposed-name/status resolution is derived once instead of re-hand-rolled per
 * consumer.
 */ export function buildSuggestionInfoMap(suggestions) {
    const map = new Map();
    for (const s of suggestions ?? []){
        map.set(String(s._id), {
            name: s.payload.name,
            status: s.status
        });
    }
    return map;
}
/**
 * Derive the display shape (proposed `name` + `isDeclined`) from a resolved
 * `SuggestionRefInfo`. The single place the `rejected → declined` rule lives, so
 * the canvas placeholder node and the step-detail panels read it identically.
 */ export function suggestionRefInfoToDisplay(info) {
    return {
        name: info.name,
        isDeclined: info.status === "rejected"
    };
}
/**
 * Resolve the step recording id behind a process-flow node id.
 *
 * The builder mints a few synthetic id shapes alongside real step ids:
 *  - `end-<stepId>` / `end-<stepId>-<branchKey>` — end-of-path markers.
 *    Returns `null`; callers that need the predecessor step should read
 *    `node.data.predecessorStepId` directly.
 *  - `gateway-<stepId>` — a fork gateway synthesized for a task with
 *    multiple outbound paths. Returns `<stepId>`.
 *  - `gateway-<stepId>-rule-<ruleId>` — a rule-scoped parallel gateway
 *    synthesized when a decision rule output fans out to multiple
 *    consumers. Returns `<stepId>` (the owning decision step).
 *  - All other ids (step ids, `assignment-*`, `flowViralPlaceholder`
 *    draft step ids) are returned unchanged.
 *
 * This helper is the single source of truth for that mapping; any caller
 * that needs to reach back to the step recording should use it instead of
 * re-parsing id prefixes.
 */ export function resolveStepIdFromNodeId(nodeId) {
    if (isEndNodeId(nodeId)) return null;
    const gateway = parseGatewayNodeId(nodeId);
    if (gateway) return gateway.stepId;
    return nodeId;
}
/**
 * Get the ID of the previous node (step or start event node), handling both
 * string and populated object cases. Returns null when unset.
 */ function getPreviousStepId(previousStep) {
    if (!previousStep) {
        return null;
    }
    if (typeof previousStep === "object") {
        return previousStep._id;
    }
    return previousStep;
}
/**
 * Determine the swimlane for a step based on its assignee
 */ function getDepartmentFromUser(user) {
    // Some callers receive a populated user with `department` still as an
    // ObjectId string (the owning query didn't recurse into department).
    // Guard so we don't synthesize a swimlane with `label: undefined`, which
    // crashes the downstream `swimlaneOrder.sort` on `localeCompare`.
    if (user.department && typeof user.department === "object") {
        return {
            key: `dept-${user.department._id}`,
            label: user.department.name,
            laneType: "department"
        };
    }
    return null;
}
// A factory, not a shared constant: assembly mutates lane infos in place
// (e.g. the lane-color stamping pass), so a module-level singleton would leak
// state across processes and re-renders.
const unassignedLane = ()=>({
        key: "unassigned",
        label: "Unassigned",
        laneType: "unassigned"
    });
function teamSwimlane(team) {
    // A team with a populated department resolves to the department lane — the
    // lane identity is the department, so the team's tombstone never marks it.
    return getDepartmentFromUser(team) ?? {
        key: `team-${team._id}`,
        label: team.name,
        laneType: "team",
        isDeleted: isAssigneeDeleted(team)
    };
}
export function getSwimlaneInfo(step, lookup) {
    const { assignedTo, assignedToModel } = step;
    const recorderLane = ()=>{
        const recordedBy = step.recordedBy;
        if (recordedBy) return getDepartmentFromUser(recordedBy) ?? unassignedLane();
        return unassignedLane();
    };
    if (!assignedTo || !assignedToModel) return recorderLane();
    // Normalize both transport shapes into an `entity` the switch can dispatch
    // on. Suggestion refs have no entity → recorder.
    let entity;
    let entityId;
    if (typeof assignedTo === "object" && "_id" in assignedTo) {
        entity = assignedTo;
        entityId = entity._id;
    } else if (assignedTo.type === "entity") {
        entityId = assignedTo.id;
        entity = lookupAssignee(assignedTo, assignedToModel, lookup);
    } else {
        return recorderLane();
    }
    const namedLane = (laneType)=>({
            key: `${laneType}-${entityId}`,
            label: entity?.name ?? entityId,
            laneType,
            isDeleted: isAssigneeDeleted(entity)
        });
    switch(assignedToModel){
        case "User":
            return entity ? getDepartmentFromUser(entity) ?? unassignedLane() : recorderLane();
        case "OrgFunction":
            return entity ? teamSwimlane(entity) : recorderLane();
        case "System":
            return namedLane("system");
        case "ExternalContact":
            return namedLane("external");
        default:
            return unassignedLane();
    }
}
/**
 * Two-pass swimlane resolver: assignee-derived lane wins, then `laneKey` hint
 * (only honored when another step has already contributed that lane via a real
 * assignee), then `unassigned` as the default.
 *
 * Callers must build `knownLanes` upstream by running `getSwimlaneInfo` over
 * every step in the draft and registering each non-`unassigned` result keyed
 * by `info.key`. Edge-actions use this to mirror the builder's precedence
 * when picking a lane hint to forward to a freshly-created step.
 */ export function resolveStepSwimlane(step, knownLanes, lookup) {
    const derived = getSwimlaneInfo(step, lookup);
    if (derived.laneType !== "unassigned") return derived;
    if (step.laneKey && knownLanes.has(step.laneKey)) {
        return knownLanes.get(step.laneKey);
    }
    return derived;
}
/**
 * Does the events channel carry anything worth rendering on its own (i.e.
 * with zero steps/placeholders)?
 *
 * A START event counts only with content: every process owns a blank start
 * event from birth, and it alone must keep the empty state. An INTERMEDIATE
 * event counts, blank or not, only when `showUnattachedIntermediateEvents` is
 * on — otherwise it renders only when a step input attaches to it.
 */ export function hasRenderableEventContent(events, showUnattachedIntermediateEvents = false) {
    if (!events || events.length === 0) return false;
    return events.some((ev)=>ev.eventKind === "start" ? Boolean(ev.name || ev.description || ev.eventType && ev.eventType !== "none") : showUnattachedIntermediateEvents);
}
/**
 * Does the input carry a `sourceSteps` tag naming a start event node? The
 * post-M2 form of the retired `'START'` tag: such an input is served by the
 * start node itself, so the event passes must not mint a separate stray
 * circle for it.
 */ function hasStartTaggedSource(input, startEventIds) {
    if (startEventIds.size === 0) return false;
    return (input.sourceSteps ?? []).some((s)=>startEventIds.has(String(s?.stepId)));
}
function isExplicitEndPath(successors) {
    const status = successors?.successorStatus;
    return status === "NONE" || status === "UNCLEAR";
}
/**
 * Synchronously assemble the process flow graph: nodes (with swimlane container
 * placeholders + `parentId` on children), edges, and the swimlane map/order.
 *
 * Positions are seeded from the optional `nodePositions` map (keyed by node id).
 * Nodes without a position entry are placed at `{0,0}` — callers that need
 * meaningful coordinates must either pass `nodePositions` or run a layout step
 * afterward (`applySwimlaneLayout` async / `applyLaneLayout` sync).
 */ export function assembleProcessFlow(process, nodePositions = new Map(), options = {}) {
    const rawOpenAssignments = options.openAssignments ?? [];
    // PENDING_UNCLEAR escalations share their `stableStepId` with the step they
    // escalate from. When that originating step is present as a recorded step
    // in this same assembly (the editor's completed-step view), the amber
    // end-node already represents the state visually — so we suppress the
    // placeholder to avoid overlaying the node. When no recorded step matches
    // (the preview-during-recording flow), we render the placeholder with the
    // unclear variant. Skip the Set allocation + filter entirely when there are
    // no unclear escalations (the common case).
    const hasUnclear = rawOpenAssignments.some((a)=>a.status === "pending-unclear");
    let openAssignments;
    if (hasUnclear) {
        const recordedStepIds = new Set();
        for (const step of process?.processStepRecordings ?? []){
            recordedStepIds.add(step._id);
        }
        openAssignments = rawOpenAssignments.filter((a)=>a.status !== "pending-unclear" || !recordedStepIds.has(String(a.stableStepId)));
    } else {
        openAssignments = rawOpenAssignments;
    }
    // Classify the events channel. The exhaustive `nodeKind` switch guarantees
    // a future node kind on the channel fails compilation (and loudly at
    // runtime) instead of being silently skipped.
    const channelEvents = options.events ?? [];
    const startEvents = [];
    for (const ev of channelEvents){
        const nodeKind = ev.nodeKind ?? "event";
        switch(nodeKind){
            case "event":
                if (ev.eventKind === "start") startEvents.push(ev);
                break;
            default:
                {
                    nodeKind;
                    throw new Error(`[process-flow] unhandled node kind on events channel: ${String(nodeKind)}`);
                }
        }
    }
    const startEventIds = new Set(startEvents.map((ev)=>String(ev._id)));
    const hasSteps = (process?.processStepRecordings?.length ?? 0) > 0;
    const hasEventContent = hasRenderableEventContent(channelEvents, options.showUnattachedIntermediateEvents);
    const hasPlaceholders = openAssignments.length > 0;
    const showsBlankStart = options.showBlankStartEvent === true && startEvents.length > 0;
    if (!process || !hasSteps && !hasPlaceholders && !hasEventContent && !showsBlankStart) {
        return {
            nodes: [],
            edges: [],
            nodeSwimlaneMap: new Map(),
            swimlaneOrder: []
        };
    }
    const steps = process.processStepRecordings ?? [];
    const nodes = [];
    const edges = [];
    const positionFor = (id)=>{
        const saved = nodePositions.get(id);
        return saved ? {
            x: saved.x,
            y: saved.y
        } : {
            x: 0,
            y: 0
        };
    };
    // Track which swimlane each node belongs to
    const nodeSwimlaneMap = new Map();
    // Track swimlane metadata (ordered by first appearance)
    const swimlaneOrder = [];
    const swimlaneSet = new Set();
    // Two-pass resolver: Pass A collects every lane a real assignee contributes
    // so Pass B can elevate `laneKey`-hinted (assignee-less) steps out of
    // `unassigned` into one of those lanes. Pass A's derived info is reused in
    // Pass B to avoid re-running `getSwimlaneInfo` on every step.
    const stepSwimlaneMap = new Map();
    const registerSwimlane = (info)=>{
        if (!swimlaneSet.has(info.key)) {
            swimlaneSet.add(info.key);
            swimlaneOrder.push(info);
        }
    };
    const knownLanes = new Map();
    const derivedInfos = new Array(steps.length);
    const { assigneeLookup, suggestionsById } = options;
    steps.forEach((step, i)=>{
        const info = getSwimlaneInfo(step, assigneeLookup);
        derivedInfos[i] = info;
        if (info.laneType !== "unassigned") {
            knownLanes.set(info.key, info);
        }
    });
    steps.forEach((step, i)=>{
        const derived = derivedInfos[i];
        const info = derived.laneType === "unassigned" && step.laneKey ? knownLanes.get(step.laneKey) ?? derived : derived;
        stepSwimlaneMap.set(step._id, info);
        registerSwimlane(info);
    });
    // Placeholder nodes need swimlanes too — derive from the populated
    // assignee so they land in the correct lane. Keyed by `stableStepId`
    // (the node id downstream steps reference).
    const assignmentSwimlaneMap = new Map();
    openAssignments.forEach((assignment)=>{
        const info = getSwimlaneInfo({
            assignedTo: assignment.assignedTo,
            assignedToModel: assignment.assignedToModel
        }, assigneeLookup);
        assignmentSwimlaneMap.set(String(assignment.stableStepId), info);
        registerSwimlane(info);
    });
    // Sort swimlanes: departments first, then teams, systems, external, unassigned
    const laneTypePriority = {
        department: 0,
        team: 1,
        system: 2,
        external: 3,
        unassigned: 4
    };
    const defaultLaneSort = (a, b)=>{
        const pa = laneTypePriority[a.laneType] ?? 5;
        const pb = laneTypePriority[b.laneType] ?? 5;
        if (pa !== pb) return pa - pb;
        return a.label.localeCompare(b.label);
    };
    const override = options.swimlaneOrderOverride;
    if (override && override.length > 0) {
        // Lanes in the override come first (in override order); lanes not listed
        // are appended via the default rule so newly-introduced lanes don't
        // disturb the user's saved sequence.
        const overrideIndex = new Map();
        override.forEach((key, idx)=>overrideIndex.set(key, idx));
        const inOverride = [];
        const remaining = [];
        swimlaneOrder.forEach((info)=>{
            if (overrideIndex.has(info.key)) inOverride.push(info);
            else remaining.push(info);
        });
        inOverride.sort((a, b)=>(overrideIndex.get(a.key) ?? 0) - (overrideIndex.get(b.key) ?? 0));
        remaining.sort(defaultLaneSort);
        swimlaneOrder.splice(0, swimlaneOrder.length, ...inOverride, ...remaining);
    } else {
        swimlaneOrder.sort(defaultLaneSort);
    }
    const laneColorByKey = new Map((options.laneColorsOverride ?? []).map((entry)=>[
            entry.laneKey,
            entry.color
        ]));
    swimlaneOrder.forEach((info)=>{
        info.color = laneColorByKey.get(info.key);
    });
    // Create the start node(s) — top-level, no swimlane (lane anchoring runs
    // after the edges are known). Each start event renders as a real node
    // under its own id, carrying its own metadata. With no start event on the
    // channel (orphan-repair processes) no start node renders at all — the
    // degraded state migration M2 logged for manual repair.
    const primaryStartNodeId = startEvents[0] ? String(startEvents[0]._id) : undefined;
    for (const ev of startEvents){
        const nodeId = String(ev._id);
        nodes.push({
            id: nodeId,
            type: "flowStart",
            position: positionFor(nodeId),
            data: {
                canvasKind: "event-start",
                label: ev.name || "Start",
                description: ev.description,
                eventType: ev.eventType || "none"
            }
        });
    }
    const stepGraph = new Map();
    function addEdge(sourceId, conn) {
        if (!stepGraph.has(sourceId)) stepGraph.set(sourceId, []);
        stepGraph.get(sourceId).push(conn);
    }
    // Track which steps are decisions (gateway nodes have a single unnamed handle)
    const decisionStepIds = new Set();
    // For each decision step, cache the full `groupRulesByOutput` result so
    // downstream edge resolution (step-to-step AND assignment-incoming) can
    // reuse both the representative-handle map AND the ruleId → output-label
    // map without re-grouping rules per edge.
    const decisionMetaBySource = new Map();
    steps.forEach((step)=>{
        if (step.actionType !== "decision") return;
        decisionStepIds.add(step._id);
        if (step.rules && step.rules.length > 0) {
            decisionMetaBySource.set(step._id, groupRulesByOutput(step.rules));
        }
    });
    // Expand a source's `ruleIds` into one representative per DISTINCT decision
    // output it touches. A `previous_output` source can consume several outputs
    // of the same decision through one `sourceSteps` entry — the connect handler
    // stores a whole output group per connect, and merge unions two such entries
    // (`[R_yes, R_no]`) onto one source. Each distinct output is its own
    // handle/edge, so the builder must emit one connection per output, not one
    // carrying the whole array. Rules sharing a single output collapse to one rep
    // (one handle). A non-decision source — or one with no rule tag — yields
    // `[undefined]` (a single plain edge, today's behavior); stale rule ids that
    // no longer resolve fall back to themselves (drawn with no output handle,
    // exactly as before).
    const distinctRuleRepsForSource = (sourceStepId, ruleIds)=>{
        if (!ruleIds || ruleIds.length === 0) return [
            undefined
        ];
        const meta = decisionMetaBySource.get(sourceStepId);
        const reps = [];
        const seen = new Set();
        for (const ruleId of ruleIds){
            const rep = meta?.ruleIdToRepresentative.get(ruleId) ?? ruleId;
            if (seen.has(rep)) continue;
            seen.add(rep);
            reps.push(rep);
        }
        return reps;
    };
    // Target ids that render as viral placeholders (unnamed single handle).
    // Edges to these must not carry `targetHandle: handle-0` — the handle
    // doesn't exist and the edge would be orphaned. Placeholder nodes use
    // `stableStepId` as the React Flow id, so downstream steps' input
    // references resolve against the same key.
    const viralTargetNodeIds = new Set();
    openAssignments.forEach((assignment)=>{
        viralTargetNodeIds.add(String(assignment.stableStepId));
    });
    // A downstream edge from a decision source with no rule tag is "unbound":
    // the connection predates the decision's rules (e.g. a task that was
    // switched to a decision) or outlived the rule it was tagged with. It can't
    // belong to any specific output, so we drop it from the graph entirely.
    // Drawing it would reattach the edge to the first rule's handle — a phantom
    // successor the rule doesn't own (its `successors` bucket stays empty, so
    // publish validation rejects it). Task sources are never unbound; their
    // edges legitimately carry no `ruleIds`.
    const isUnboundDecisionEdge = (sourceStepId, rawRuleIds)=>decisionStepIds.has(sourceStepId) && !(rawRuleIds && rawRuleIds.length > 0);
    steps.forEach((step)=>{
        const inputs = step.inputs ?? [];
        // A step that carries any `previous_output` input has authoritative data
        // about its predecessors via `sourceSteps` — empty `sourceSteps` means the
        // input is intentionally unlinked (e.g. the source is from a different
        // process), not "fall back to chronological previousStep". Only steps with
        // zero `previous_output` inputs can be true legacy data, where the
        // previousStep fallback is the only signal we have.
        const hasPreviousOutputInput = inputs.some((i)=>i.inputType === "previous_output");
        inputs.forEach((input, inputIndex)=>{
            // Event inputs carry sourceSteps when tagged with START during draft seeding
            const sources = "sourceSteps" in input ? input.sourceSteps ?? [] : [];
            for (const source of sources){
                const sourceStepId = String(source.stepId);
                if (isUnboundDecisionEdge(sourceStepId, source.ruleIds)) continue;
                // One connection per distinct output the source touches, so two
                // outputs of the same decision folded onto one source render as two
                // edges (each on its own handle) instead of collapsing to the first.
                for (const rep of distinctRuleRepsForSource(sourceStepId, source.ruleIds)){
                    addEdge(sourceStepId, {
                        targetStepId: step._id,
                        targetInputIndex: inputIndex,
                        ruleIds: rep ? [
                            rep
                        ] : undefined
                    });
                }
            }
        });
        if (!hasPreviousOutputInput) {
            const prevStepId = getPreviousStepId(step.previousStep);
            // A start event node id lands here too — its bucket is consumed by the
            // start-connection pass below, exactly like a step-source bucket is
            // consumed by the step loop.
            //
            // Guard the same hazard the `sourceSteps` loop above guards: a stale
            // `previousStep` pointing at a decision step would synthesize a phantom
            // successor the rule doesn't own — and unlike a real edge it has no
            // backing input the disconnect handler could remove (ADR 0006).
            if (prevStepId && !isUnboundDecisionEdge(prevStepId, undefined)) {
                addEdge(prevStepId, {
                    targetStepId: step._id
                });
            }
        }
    });
    // sourceStepId → assignment node ids consuming that step's output (de-duped).
    const stepAssignmentConsumers = new Map();
    // sourceStepId → representative rule id → set of assignment node ids.
    // Keyed by representative (group.firstRuleId) so decision rule groups can
    // count per-rule consumers in O(1) when deciding whether to synthesize a
    // rule-scoped parallel gateway. Non-decision sources contribute nothing
    // here (source.ruleIds is undefined on task-step edges).
    const assignmentConsumersByStepAndRule = new Map();
    openAssignments.forEach((assignment)=>{
        const nodeId = String(assignment.stableStepId);
        for (const input of assignment.inputs ?? []){
            const sources = "sourceSteps" in input ? input.sourceSteps ?? [] : [];
            for (const src of sources){
                const key = String(src.stepId);
                // An unbound decision edge isn't a real consumer — skip it entirely so
                // it neither inflates the fan-out count nor draws a phantom edge.
                if (isUnboundDecisionEdge(key, src.ruleIds)) continue;
                if (!stepAssignmentConsumers.has(key)) {
                    stepAssignmentConsumers.set(key, new Set());
                }
                stepAssignmentConsumers.get(key).add(nodeId);
                // Count this consumer once per distinct output it consumes, so a
                // source feeding the assignment from two outputs of the same decision
                // contributes to each output's per-rule fan-out count independently.
                for (const rep of distinctRuleRepsForSource(key, src.ruleIds)){
                    if (!rep) continue;
                    let perStep = assignmentConsumersByStepAndRule.get(key);
                    if (!perStep) {
                        perStep = new Map();
                        assignmentConsumersByStepAndRule.set(key, perStep);
                    }
                    let perRule = perStep.get(rep);
                    if (!perRule) {
                        perRule = new Set();
                        perStep.set(rep, perRule);
                    }
                    perRule.add(nodeId);
                }
            }
        }
    });
    // Task steps that got a synthesized fan-out gateway; assignment edges rooted
    // at one of these steps must re-source to `gateway-<stepId>`.
    const stepsWithGateway = new Set();
    // Decision steps → representative rule ids whose rule output fans out to
    // 2+ consumers and thus got a synthesized `gateway-<stepId>-rule-<repId>`.
    // Drives consumer re-sourcing on the decision branch and in the assignment
    // incoming loop.
    const rulesWithGateway = new Map();
    // Resolve the correct edge source + decoration for a rule-tagged consumer of
    // a decision (or plain task) step. When the rule has a synthesized gateway,
    // the consumer attaches to the gateway with no handle/label (those ride the
    // beforeFork edge). Otherwise the rule handle + output label stay on the
    // direct edge. The `stepsWithGateway` re-source handles the non-decision
    // fallback used by the placeholder-incoming loop.
    const resolveRuleEdgeSource = (sourceStepId, rawRuleId)=>{
        const meta = decisionMetaBySource.get(sourceStepId);
        const rep = rawRuleId && meta ? meta.ruleIdToRepresentative.get(rawRuleId) : undefined;
        if (rep && rulesWithGateway.get(sourceStepId)?.has(rep)) {
            return {
                source: buildGatewayNodeId(sourceStepId, rep)
            };
        }
        const label = rawRuleId && meta ? meta.ruleIdToOutput.get(rawRuleId) : undefined;
        const source = stepsWithGateway.has(sourceStepId) ? buildGatewayNodeId(sourceStepId) : sourceStepId;
        return {
            source,
            sourceHandle: rep ? buildRuleHandleId(rep) : undefined,
            label
        };
    };
    // Create nodes for each step. zIndex follows array order so later-created
    // steps consistently stack above earlier ones when positions overlap —
    // without this, React Flow's internal sort (parent/child + selection) can
    // flip the DOM order once enough nodes accumulate.
    steps.forEach((step, stepIndex)=>{
        const isDecision = step.actionType === "decision";
        const laneInfo = stepSwimlaneMap.get(step._id);
        const zIndex = stepIndex + 1;
        if (isDecision) {
            const gatewayNode = {
                id: step._id,
                type: "flowGateway",
                position: positionFor(step._id),
                zIndex,
                data: {
                    canvasKind: "decision",
                    type: "exclusive",
                    stepRecording: step
                }
            };
            nodes.push(gatewayNode);
        } else {
            const activityNode = {
                id: step._id,
                type: "flowActivity",
                position: positionFor(step._id),
                zIndex,
                data: {
                    canvasKind: "step",
                    stepRecording: step
                }
            };
            nodes.push(activityNode);
        }
        nodeSwimlaneMap.set(step._id, laneInfo.key);
    });
    // Resolve which target handle a connection should attach to.
    // Activity nodes create named handles (handle-0, handle-1, ...) per input.
    // Gateway and viral-placeholder nodes have a single unnamed handle, so
    // targetHandle must be undefined for them.
    function getTargetHandle(conn) {
        if (decisionStepIds.has(conn.targetStepId)) return undefined;
        if (viralTargetNodeIds.has(conn.targetStepId)) return undefined;
        if (conn.targetInputIndex != null) return `handle-${conn.targetInputIndex}`;
        return undefined;
    }
    // Keyed by event id (or by the synthetic node id for eventId-less inputs)
    // so input-derived targets merge onto channel entries regardless of which
    // node-id shape the entry renders under.
    const emittedEvents = new Map();
    for (const ev of channelEvents){
        const eventIdStr = String(ev._id);
        emittedEvents.set(eventIdStr, {
            nodeId: eventIdStr,
            kind: ev.eventKind,
            eventId: eventIdStr,
            name: ev.name,
            description: ev.description,
            eventType: ev.eventType,
            targets: []
        });
    }
    // Merge one event input into `emittedEvents`, keyed by `eventId` (falling
    // back to a per-target synthetic id for legacy inputs that predate
    // first-class event nodes). Shared by the step and assignment passes.
    // Channel entries win on metadata — the event entity is the source of
    // truth; input snapshots only serve events absent from the channel.
    const registerEventInputTarget = (ev, targetStepId, inputIndex)=>{
        const eventIdStr = ev.eventId ? String(ev.eventId) : undefined;
        const syntheticNodeId = `${EVENT_NODE_ID_PREFIX}${targetStepId}-${inputIndex}`;
        const key = eventIdStr ?? syntheticNodeId;
        const existing = emittedEvents.get(key);
        if (existing) {
            existing.targets.push({
                stepId: targetStepId,
                inputIndex
            });
        } else {
            emittedEvents.set(key, {
                nodeId: eventIdStr ? buildEventNodeId(eventIdStr) : syntheticNodeId,
                kind: "legacy",
                eventId: eventIdStr,
                name: ev.name ?? "",
                description: ev.description ?? "",
                eventType: ev.type ?? "none",
                targets: [
                    {
                        stepId: targetStepId,
                        inputIndex
                    }
                ]
            });
        }
    };
    steps.forEach((step)=>{
        (step.inputs ?? []).forEach((input, inputIndex)=>{
            if (input.inputType !== "event") return;
            // An input tagged with a start node id is served by the start node
            // itself (the post-M2 form of the retired START tag) — minting a
            // separate stray circle for it would double the start event.
            if (hasStartTaggedSource(input, startEventIds)) {
                return;
            }
            registerEventInputTarget(input, step._id, inputIndex);
        });
    });
    // Assignments carry event inputs too (a converted step keeps its
    // start-event edges) — same start-tag suppression.
    openAssignments.forEach((assignment)=>{
        const nodeId = String(assignment.stableStepId);
        (assignment.inputs ?? []).forEach((input, inputIndex)=>{
            if (input.inputType !== "event" || hasStartTaggedSource(input, startEventIds)) {
                return;
            }
            registerEventInputTarget(input, nodeId, inputIndex);
        });
    });
    // Emit the collected events through the exhaustive kind switch. Circle
    // events land in the lane of their first attached target step so the saved
    // position reads consistently; floating legacy events (no targets) stay
    // top-level — autolayout handles them.
    const pushEventTargetEdges = (emitted)=>{
        for (const target of emitted.targets){
            const isDecision = decisionStepIds.has(target.stepId);
            const isViral = viralTargetNodeIds.has(target.stepId);
            const targetHandle = isDecision || isViral ? undefined : `handle-${target.inputIndex}`;
            edges.push(createEdge(emitted.nodeId, target.stepId, {
                targetHandle
            }));
        }
    };
    // Anchor an event node in the lane of its first attached target. The
    // target can be a step or an assignment placeholder — lanes for the two
    // node kinds live in separate maps.
    const anchorEventInFirstTargetLane = (emitted)=>{
        const first = emitted.targets[0];
        if (!first) return;
        const lane = stepSwimlaneMap.get(first.stepId) ?? assignmentSwimlaneMap.get(first.stepId);
        if (lane) nodeSwimlaneMap.set(emitted.nodeId, lane.key);
    };
    const pushCircleEventNode = (emitted)=>{
        nodes.push({
            id: emitted.nodeId,
            type: FLOW_EVENT_NODE_TYPE,
            position: positionFor(emitted.nodeId),
            data: {
                // Circles come in two flavors: persisted intermediate event nodes
                // (raw event-id node, addressable) vs legacy event-INPUT snapshots
                // (`event-…` id, need the lazy upgrade first).
                canvasKind: emitted.kind === "legacy" ? "event-legacy" : "event-intermediate",
                label: emitted.name,
                description: emitted.description,
                eventType: emitted.eventType,
                eventId: emitted.eventId
            }
        });
        anchorEventInFirstTargetLane(emitted);
        pushEventTargetEdges(emitted);
    };
    emittedEvents.forEach((emitted)=>{
        switch(emitted.kind){
            case "start":
                {
                    // The node itself was already pushed (ahead of the steps). Here it
                    // gets its eventId-attached edges; the primary's lane anchoring runs
                    // below with the start-connection semantics, secondary starts anchor
                    // like circles do.
                    if (emitted.nodeId !== primaryStartNodeId) {
                        anchorEventInFirstTargetLane(emitted);
                    }
                    pushEventTargetEdges(emitted);
                    break;
                }
            case "intermediate":
                {
                    // Published read surfaces derive event visibility from attachments
                    // (publish validation forbids floating events) — an unreferenced
                    // intermediate node must not surface as a floating circle. The draft
                    // editor opts in via `showUnattachedIntermediateEvents`: there a
                    // floating event is a legitimate editing state.
                    if (emitted.targets.length === 0 && !options.showUnattachedIntermediateEvents) {
                        return;
                    }
                    pushCircleEventNode(emitted);
                    break;
                }
            case "legacy":
                {
                    pushCircleEventNode(emitted);
                    break;
                }
            default:
                {
                    emitted.kind;
                    throw new Error(`[process-flow] unhandled event kind: ${String(emitted.kind)}`);
                }
        }
    });
    // Connect each start node to steps whose graph bucket names it — a first
    // step with no previous_output inputs contributes its node-id previousStep
    // there, mirroring how step-source buckets are consumed by the step loop.
    // Targets already covered by an eventId attachment to the same start node
    // (the canonical first-step linkage — pushEventTargetEdges drew their edge)
    // are skipped, so a step carrying both anchor forms renders ONE start edge.
    const startConnections = primaryStartNodeId ? stepGraph.get(primaryStartNodeId) ?? [] : [];
    for (const ev of startEvents){
        const sourceId = String(ev._id);
        const coveredByAttachment = new Set((emittedEvents.get(sourceId)?.targets ?? []).map((t)=>t.stepId));
        for (const conn of stepGraph.get(sourceId) ?? []){
            if (coveredByAttachment.has(conn.targetStepId)) continue;
            edges.push(createEdge(sourceId, conn.targetStepId, {
                targetHandle: getTargetHandle(conn)
            }));
        }
    }
    // Find the first placeholder whose inputs reference the start (a source
    // tag naming a start node, or an eventId-linked start input) so the
    // lane-placement pass below can anchor the start node in its lane when
    // there's no recorded step to follow (e.g. a draft with only a
    // placeholder). Only the first match is needed — stop at the first hit.
    let firstPlaceholderStartTarget;
    for (const assignment of openAssignments){
        const referencesStart = (assignment.inputs ?? []).some((input)=>hasStartTaggedSource(input, startEventIds) || input.inputType === "event" && input.eventId != null && startEventIds.has(String(input.eventId)));
        if (referencesStart) {
            firstPlaceholderStartTarget = String(assignment.stableStepId);
            break;
        }
    }
    if (!primaryStartNodeId) {
    // No start node on the channel (orphan-repair process) — nothing to
    // anchor.
    } else if (startConnections.length > 0) {
        const lane = stepSwimlaneMap.get(startConnections[0].targetStepId);
        if (lane) nodeSwimlaneMap.set(primaryStartNodeId, lane.key);
    } else if (firstPlaceholderStartTarget) {
        // No recorded step consumes the start, but a placeholder does. Place the
        // start node in that placeholder's lane so ELK's layered layout keeps
        // them in the same horizontal band — otherwise it floats at the
        // top-level y-center and visually overlaps lane children (ELK x=0 +
        // top-level centering).
        const lane = assignmentSwimlaneMap.get(firstPlaceholderStartTarget);
        if (lane) nodeSwimlaneMap.set(primaryStartNodeId, lane.key);
    } else if (steps.length > 0) {
        // Keep the start node in a lane even with no outgoing start edge. Without
        // a parent lane it becomes a top-level node; its saved position is stored
        // lane-relative (y = rf.y - upGrowth), so reading it back as absolute
        // shifts the node by upGrowth — a visible jump the moment the user
        // deletes the start→first-step edge. Fall back to the lane of the first
        // step carrying an event input (the former first step keeps its event
        // input after a disconnect). This preserves the frame of reference so the
        // start node's stored position keeps its meaning.
        const firstEventStep = steps.find((s)=>(s.inputs ?? []).some((i)=>i.inputType === "event")) ?? steps[0];
        const lane = stepSwimlaneMap.get(firstEventStep._id);
        if (lane) nodeSwimlaneMap.set(primaryStartNodeId, lane.key);
    }
    // Process step connections and end nodes. Placeholder nodes (draft or
    // open assignments) never land here — they have no `task.successors` or
    // `rules`; their inbound edges come from the target-step pass above via
    // `addEdge` on each input's `sourceSteps[].stepId`, and their outbound
    // edges come from downstream steps' inputs referencing the placeholder's
    // `stableStepId`.
    steps.forEach((step)=>{
        // A decision keeps its inactive `task` subdoc across an actionType toggle;
        // reading its stale successors here would mint a phantom end node.
        // Deliberately `!== "decision"`, not `=== "task"` (which the sidebar and
        // the entity aggregation use): those surfaces suppress content, so failing
        // closed on an actionType-less doc is safe, while here failing closed would
        // silently drop a real end node. Don't "fix" the asymmetry.
        const successors = step.actionType === "decision" ? undefined : step.task?.successors;
        const rules = step.rules;
        const connections = stepGraph.get(step._id) || [];
        const laneInfo = stepSwimlaneMap.get(step._id);
        if (step.actionType === "decision" && rules && rules.length > 0) {
            const meta = decisionMetaBySource.get(step._id);
            if (!meta) return;
            const { groups, ruleIdToRepresentative } = meta;
            const ruleById = new Map(rules.filter((r)=>Boolean(r._id)).map((r)=>[
                    r._id,
                    r
                ]));
            // Bucket connections by their representative rule up-front so the per-
            // group fan-out count is O(1) instead of re-filtering all connections
            // for every group (`O(G × C)` → `O(C + G)`).
            const connectionCountByGroup = new Map();
            for (const conn of connections){
                const rid = conn.ruleIds?.[0];
                const rep = rid ? ruleIdToRepresentative.get(rid) : undefined;
                if (!rep) continue;
                connectionCountByGroup.set(rep, (connectionCountByGroup.get(rep) ?? 0) + 1);
            }
            groups.forEach((group, index)=>{
                const repRule = ruleById.get(group.firstRuleId);
                if (!repRule) return;
                // Sum per-group outbound paths to decide whether to synthesize a
                // rule-scoped parallel gateway (≥2 consumers of this rule's output).
                const connectionCount = connectionCountByGroup.get(group.firstRuleId) ?? 0;
                const assignmentCount = assignmentConsumersByStepAndRule.get(step._id)?.get(group.firstRuleId)?.size ?? 0;
                const outboundCount = connectionCount + assignmentCount + (isExplicitEndPath(repRule.successors) ? 1 : 0);
                let gatewayId;
                if (outboundCount > 1) {
                    gatewayId = buildGatewayNodeId(step._id, group.firstRuleId);
                    nodes.push({
                        id: gatewayId,
                        type: "flowGateway",
                        position: positionFor(gatewayId),
                        data: {
                            canvasKind: "synthetic-gateway",
                            type: "parallel"
                        }
                    });
                    edges.push(createEdge(step._id, gatewayId, {
                        sourceHandle: buildRuleHandleId(group.firstRuleId),
                        label: group.output || `Rule ${index + 1}`
                    }));
                    nodeSwimlaneMap.set(gatewayId, laneInfo.key);
                    let gwRules = rulesWithGateway.get(step._id);
                    if (!gwRules) {
                        gwRules = new Set();
                        rulesWithGateway.set(step._id, gwRules);
                    }
                    gwRules.add(group.firstRuleId);
                }
                // When a gateway fronts this rule, the beforeFork edge already carries
                // the rule handle + label; successors continue off the gateway as
                // plain afterFork edges. Without a gateway, the rule handle + label
                // ride the end-node edge directly off the decision step.
                const successorEdge = gatewayId ? {
                    sourceId: gatewayId
                } : {
                    sourceId: step._id,
                    sourceHandle: buildRuleHandleId(group.firstRuleId),
                    edgeLabel: group.output || `Rule ${index + 1}`
                };
                processSuccessors({
                    successors: repRule.successors,
                    ...successorEdge,
                    predecessorStepId: step._id,
                    branchKey: group.firstRuleId,
                    nodes,
                    edges,
                    nodeSwimlaneMap,
                    laneKey: laneInfo.key,
                    positionFor
                });
            });
            connections.forEach((conn)=>{
                const resolved = resolveRuleEdgeSource(step._id, conn.ruleIds?.[0]);
                edges.push(createEdge(resolved.source, conn.targetStepId, {
                    label: resolved.label,
                    sourceHandle: resolved.sourceHandle,
                    targetHandle: getTargetHandle(conn)
                }));
            });
        } else if (successors) {
            // A real consumer (step or assignment) overrides a stale NONE/UNCLEAR:
            // `recomputeSuccessorsForStep` can lag behind edits, and BPMN semantics
            // require a parallel gateway whenever one output feeds 2+ consumers.
            const assignmentCount = stepAssignmentConsumers.get(step._id)?.size ?? 0;
            const hasRealConsumer = connections.length > 0 || assignmentCount > 0;
            const hasEndPath = isExplicitEndPath(successors) && !hasRealConsumer;
            const outboundCount = connections.length + assignmentCount + (hasEndPath ? 1 : 0);
            let edgeSource = step._id;
            if (outboundCount > 1) {
                const gatewayId = buildGatewayNodeId(step._id);
                nodes.push({
                    id: gatewayId,
                    type: "flowGateway",
                    position: positionFor(gatewayId),
                    data: {
                        canvasKind: "synthetic-gateway",
                        type: "parallel"
                    }
                });
                edges.push(createEdge(step._id, gatewayId));
                nodeSwimlaneMap.set(gatewayId, laneInfo.key);
                stepsWithGateway.add(step._id);
                edgeSource = gatewayId;
            }
            if (hasEndPath) {
                processSuccessors({
                    successors,
                    sourceId: edgeSource,
                    predecessorStepId: step._id,
                    nodes,
                    edges,
                    nodeSwimlaneMap,
                    laneKey: laneInfo.key,
                    positionFor
                });
            }
            connections.forEach((conn)=>{
                edges.push(createEdge(edgeSource, conn.targetStepId, {
                    targetHandle: getTargetHandle(conn)
                }));
            });
        } else {
            // No ACTIVE successors bucket (a decision with 0 rules lands here too,
            // its inactive task.successors gated out above): just connect downstream
            // steps via inputs
            connections.forEach((conn)=>{
                edges.push(createEdge(step._id, conn.targetStepId, {
                    targetHandle: getTargetHandle(conn)
                }));
            });
        }
    });
    // Placeholder nodes: draft assignments (pre-publish, editable) and
    // in-flight invitations on the published process. Rendered after step
    // iteration so they don't participate in successor recompute logic.
    // Each placeholder's React Flow `id` equals its `stableStepId`, so
    // downstream steps' `inputs[].sourceSteps[].stepId` lookups resolve
    // uniformly against the same id space as recorded steps. Incoming edges
    // are drawn from `inputs[].sourceSteps` (fan-in preserved).
    openAssignments.forEach((assignment, index)=>{
        const nodeId = String(assignment.stableStepId);
        const laneInfo = assignmentSwimlaneMap.get(nodeId);
        const data = {
            canvasKind: "assignment",
            kind: assignment.kind ?? "open-assignment",
            assignee: assigneeFromPopulated(assignment.assignedTo, assignment.assignedToModel, assigneeLookup, suggestionsById),
            status: assignment.status,
            variant: assignment.status === "pending-unclear" ? "unclear" : undefined
        };
        nodes.push({
            id: nodeId,
            type: "flowViralPlaceholder",
            position: positionFor(nodeId),
            // After all step nodes so placeholders sit on top when overlapping.
            zIndex: steps.length + index + 1,
            data
        });
        nodeSwimlaneMap.set(nodeId, laneInfo.key);
        const incoming = [];
        for (const input of assignment.inputs ?? []){
            const sources = "sourceSteps" in input ? input.sourceSteps ?? [] : [];
            for (const src of sources){
                const source = String(src.stepId);
                // Mirror the stepGraph/assignment-consumer passes: an unbound decision
                // edge is dropped, never painted onto the first rule's handle.
                if (isUnboundDecisionEdge(source, src.ruleIds)) continue;
                // One incoming entry per distinct output the source touches; the
                // `${source}|${ruleId}` dedupe below then yields one edge per handle.
                for (const rep of distinctRuleRepsForSource(source, src.ruleIds)){
                    incoming.push({
                        source,
                        ruleId: rep ? String(rep) : undefined
                    });
                }
            }
        }
        // Placeholders with no inputs render as orphaned nodes — we deliberately
        // don't auto-wire them to START. When a user deletes an upstream edge,
        // an auto-reconnect to START would visibly persist as a "phantom"
        // connection after the delete succeeds server-side. Orphan state matches
        // the behavior of recorded steps with no `previousStep`.
        const seen = new Set();
        for (const { source, ruleId } of incoming){
            // Include ruleId in the dedupe key: multiple rules on the same
            // decision source can independently fan out to this assignment, and
            // each must attach to its own output handle.
            const key = `${source}|${ruleId ?? ""}`;
            if (seen.has(key)) continue;
            seen.add(key);
            // Decision source → per-output handle; task source with a synthesized
            // fan-out gateway → re-source at the gateway. `decisionMetaBySource`
            // and `stepsWithGateway` partition by step type, so never both.
            const resolved = resolveRuleEdgeSource(source, ruleId);
            edges.push(createEdge(resolved.source, nodeId, {
                sourceHandle: resolved.sourceHandle,
                label: resolved.label
            }));
        }
    });
    // Mark edges as dashed when they involve a system or external contact node
    const laneKeyToType = new Map();
    swimlaneOrder.forEach((info)=>laneKeyToType.set(info.key, info.laneType));
    const dashedLaneTypes = new Set([
        "system",
        "external"
    ]);
    edges.forEach((edge)=>{
        const sourceLane = nodeSwimlaneMap.get(edge.source);
        const targetLane = nodeSwimlaneMap.get(edge.target);
        const sourceType = sourceLane ? laneKeyToType.get(sourceLane) : undefined;
        const targetType = targetLane ? laneKeyToType.get(targetLane) : undefined;
        if (sourceType && dashedLaneTypes.has(sourceType) || targetType && dashedLaneTypes.has(targetType)) {
            edge.animated = true;
        }
    });
    // Step N: add swimlane container placeholders + set parentId on children.
    const orderedLaneKeys = swimlaneOrder.map((info)=>info.key);
    const laneExtras = new Map();
    const { onMoveSwimlaneUp, onMoveSwimlaneDown } = options;
    if (onMoveSwimlaneUp || onMoveSwimlaneDown) {
        swimlaneOrder.forEach((info, index)=>{
            const canMoveUp = index > 0;
            const canMoveDown = index < swimlaneOrder.length - 1;
            laneExtras.set(info.key, {
                onMoveUp: canMoveUp && onMoveSwimlaneUp ? ()=>onMoveSwimlaneUp(info.key, orderedLaneKeys) : undefined,
                onMoveDown: canMoveDown && onMoveSwimlaneDown ? ()=>onMoveSwimlaneDown(info.key, orderedLaneKeys) : undefined
            });
        });
    }
    const { swimlaneNodes, childrenWithParentId, topLevelNodes } = buildLanePlaceholders(nodes, nodeSwimlaneMap, swimlaneOrder, laneExtras);
    const assembledNodes = [
        ...swimlaneNodes,
        ...childrenWithParentId,
        ...topLevelNodes
    ];
    return {
        nodes: assembledNodes,
        edges,
        nodeSwimlaneMap,
        swimlaneOrder
    };
}
/**
 * Build nodes and edges for process flow visualization from process step recordings.
 * Thin async wrapper around `assembleProcessFlow` that runs ELK-based swimlane layout.
 */ export async function buildProcessFlow(process, options = {}) {
    const assembly = assembleProcessFlow(process, new Map(), options);
    if (assembly.nodes.length === 0) {
        return {
            nodes: [],
            edges: []
        };
    }
    return await applySwimlaneLayout(assembly);
}
/**
 * Process successors and create appropriate end nodes or connections
 */ function processSuccessors(params) {
    const { successors, sourceId, predecessorStepId, branchKey, nodes, edges, nodeSwimlaneMap, laneKey, edgeLabel, sourceHandle, positionFor } = params;
    if (!successors) return;
    if (successors.successorStatus === "NONE") {
        const endNode = createEndNode(i18next.t("processFlow.endLabel"), i18next.t("processFlow.noFurtherSteps"), predecessorStepId, branchKey, positionFor, "none");
        nodes.push(endNode);
        nodeSwimlaneMap.set(endNode.id, laneKey);
        edges.push(createEdge(sourceId, endNode.id, {
            label: edgeLabel,
            sourceHandle
        }));
        return;
    }
    if (successors.successorStatus === "UNCLEAR") {
        const endNode = createEndNode(i18next.t("processFlow.endLabel"), i18next.t("processFlow.unclearContinuation"), predecessorStepId, branchKey, positionFor, "unclear");
        nodes.push(endNode);
        nodeSwimlaneMap.set(endNode.id, laneKey);
        edges.push(createEdge(sourceId, endNode.id, {
            label: edgeLabel,
            sourceHandle
        }));
        return;
    }
// External contacts and systems are successor entities, not process ends — no end nodes needed
}
/**
 * Create an edge between two nodes
 */ export function createEdge(source, target, options) {
    return {
        // Explicit delimiters (`|`) prevent collisions when source/target/handle ids
        // contain hyphens (e.g. `gateway-<id>`, `end-<id>-<key>`).
        id: `e|${source}|${options?.sourceHandle ?? ""}|${target}|${options?.targetHandle ?? ""}`,
        source,
        sourceHandle: options?.sourceHandle,
        target,
        targetHandle: options?.targetHandle,
        animated: options?.animated || false,
        label: options?.label,
        style: {
            stroke: "black",
            strokeWidth: 2
        }
    };
}
function assigneeFromPopulated(populated, assignedToModel, lookup, suggestionsById) {
    if (!populated || typeof populated === "string") return null;
    // Suggestion-typed ref: no resolved entity exists yet, so resolve the
    // proposed name from the suggestion map and flag it `proposed` so the node
    // renders a dashed "proposed" ghost instead of the `??` initials fallback.
    // A `rejected` suggestion additionally flags `declined` so the node reads red
    // rather than blue. A thin ref has no `_id` (populated entities do — incl.
    // external contacts, which also carry a `type` field), so narrow on that first.
    if (!("_id" in populated) && populated.type === "suggestion") {
        const info = suggestionsById?.get(String(populated.id));
        if (!info) return null;
        const { name, isDeclined } = suggestionRefInfoToDisplay(info);
        return {
            name,
            proposed: true,
            declined: isDeclined
        };
    }
    // Draft assignments arrive as a thin `{ type, id }` ref (no `_id`) — swap
    // in the populated entity from the same lookup `getSwimlaneInfo` uses for
    // lane placement. Published flows already carry the populated entity and
    // skip the lookup.
    const entity = "_id" in populated ? populated : lookupAssignee(populated, assignedToModel, lookup);
    if (!entity) return null;
    const name = getAssigneeName({
        assignedTo: entity
    });
    const avatar = entity.avatar;
    return {
        name,
        avatar
    };
}
/**
 * Resolve an `EntityOrSuggestionRef` to its populated entity via the supplied
 * lookup. Returns `undefined` for a suggestion ref (no resolved entity yet), a
 * missing model, a missing lookup map, or an id absent from the map — callers
 * render a pending label or fall back to the bare id for that case.
 *
 * The single canonical resolver: every surface (sidebar text, swimlane labels,
 * card headers) routes through this instead of re-deriving a model→list switch.
 */ export function lookupAssignee(ref, model, lookup) {
    if (!ref || ref.type !== "entity" || !model) return undefined;
    const maps = {
        User: lookup?.users,
        OrgFunction: lookup?.orgFunctions,
        System: lookup?.systems,
        ExternalContact: lookup?.externalContacts
    };
    return maps[model]?.get(ref.id);
}
/**
 * Resolve an `EntityOrSuggestionRef` straight to a display name (user full name
 * or entity `name`). Returns `undefined` when the ref doesn't resolve, so text
 * surfaces can fall back to the bare id (`resolveRefName(...) ?? ref.id`).
 */ export function resolveRefName(ref, model, lookup) {
    const entity = lookupAssignee(ref, model, lookup);
    if (!entity) return undefined;
    return getAssigneeName({
        assignedTo: entity
    }) || undefined;
}
// TODO: Review AI Code for feature: Process Map
function createEndNode(label, reasonForEnd, predecessorStepId, branchKey, positionFor, variant) {
    const id = buildEndNodeId(predecessorStepId, branchKey);
    return {
        id,
        type: "flowEnd",
        position: positionFor(id),
        data: {
            canvasKind: "end",
            label,
            reasonForEnd,
            predecessorStepId,
            branchKey,
            variant
        }
    };
}

//# sourceMappingURL=builder.js.map